// QuestionCard.tsx
import React, { useState, useEffect } from 'react';
import { QuestionData, Option } from './types'; // Import Option for parseCorrectAnswers
import OptionItem from './OptionItem';

interface QuestionCardProps {
    questionData: QuestionData;
    isPracticeMode: boolean;
    selectedAnswer: string | string[] | undefined;
    onSelectAnswer: (
        qNo: number,
        selectedValue: string | undefined,
        isChecked?: boolean
    ) => void;
    resetFeedback: number;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
    questionData,
    isPracticeMode,
    selectedAnswer,
    onSelectAnswer,
    resetFeedback
}) => {
    const [showFeedback, setShowFeedback] = useState(false);

    // Helper to parse correct answers from the `answer` field (string or array)
    // Moved this function here from QuizApp to be locally available.
    const parseCorrectAnswers = (answer: string | string[], options: Option[]): string[] => {
        if (Array.isArray(answer)) {
            return answer.map(ans => ans.trim());
        } else if (typeof answer === 'string' && answer.length <= 10) {
            const answers = answer.split(',').map(ans => ans.trim());
            // Try to map answer keys to values if they are 'option_no'
            return answers.map(ans => {
                const option = options.find(opt => opt.option_no === ans);
                // Prioritize value if it exists, otherwise use the key itself
                return option && option.value !== undefined ? option.value : ans;
            });
        } else {
            const option = options.find(opt => opt.option_no.trim() === answer.trim());
            return option && option.value !== undefined ? [option.value] : [answer];
        }
        return [];
    };

    const correctAnswersArray = parseCorrectAnswers(questionData.answer, questionData.options);

    useEffect(() => {
        setShowFeedback(false);
    }, [questionData.qNo, resetFeedback]);

    const handleOptionSelect = (selectedValue: string | undefined, isChecked: boolean) => {
        if (!showFeedback) {
            onSelectAnswer(questionData.qNo, selectedValue, isChecked);
        }
    };

    const handleShowAnswer = () => {
        setShowFeedback(true);
    };

    // Logic to determine if a specific option is selected for passing to OptionItem
    const isOptionCurrentlySelected = (option: Option): boolean => {
        if (questionData.type === 'single') {
            // For single, compare with value or option_no
            return selectedAnswer === option.value || selectedAnswer === option.option_no;
        } else {
            // For multiple, check if option.value or option.option_no is in the selectedAnswer array
            const selectedArray = Array.isArray(selectedAnswer) ? selectedAnswer : [];
            return (option.value !== undefined && selectedArray.includes(option.value)) ||
                (option.option_no !== undefined && selectedArray.includes(option.option_no));
        }
    };


    return (
        <div className="question-card">
            <h3>Q{questionData.qNo}: {questionData.question}</h3>
            <h4>Options:</h4>
            <ul className="options-list">
                {questionData.options.map((option, index) => (
                    <OptionItem
                        key={`${questionData.qNo}-${option.option_no || index}`}
                        option={option}
                        questionNumber={questionData.qNo}
                        correctAnswers={correctAnswersArray}
                        isSelected={isOptionCurrentlySelected(option)} // Use the new helper function
                        showFeedback={showFeedback}
                        onSelect={handleOptionSelect}
                        inputType={questionData.answer.includes(',') && questionData.answer.length <= 10 ? 'multiple' : 'single'}
                    />
                ))}
            </ul>

            {isPracticeMode && !showFeedback && (
                <button onClick={handleShowAnswer} className="show-answer-button quiz-button">
                    Show Answer
                </button>
            )}

            {showFeedback && (questionData.explanation || questionData.reference) && (
                <div className="practice-mode-feedback">
                    {questionData.explanation && (
                        <p>
                            <strong>Explanation:</strong> {questionData.explanation}
                        </p>
                    )}
                    {questionData.reference && (
                        <p>
                            <strong>Reference:</strong>{' '}
                            {questionData.reference.startsWith('http') ? (
                                <a href={questionData.reference} target="_blank" rel="noopener noreferrer">
                                    {questionData.reference}
                                </a>
                            ) : (
                                questionData.reference
                            )}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionCard;